import type {
  ReleaseDetailResponse,
  ReleaseSummaryResponse,
  ReleaseTrackResponse,
  TrackArtistResponse,
} from './releasesApi';

const NEW_RELEASE_WINDOW_DAYS = 14;

export interface ReleaseSummary {
  id: number;
  slug: string;
  title: string;
  artistName: string;
  artistSlug: string;
  coverUrl: string | null;
  releaseType: string;
  releaseTypeLabel: string;
  status: ReleaseSummaryResponse['status'];
  price: number | null;
  completePrice: number | null;
  currency: string;
  tippingEnabled: boolean;
  memberOnly: boolean;
  laneLabel: string | null;
  lanes: string[];
  publishAt: string | null;
  isNew: boolean;
  isLimited: boolean;
  limitedTotal: number | null;
  limitedSold: number;
  limitedRemaining: number | null;
  owned: boolean;
  artists: ReleaseArtist[];
  ownedTrackTotal: number;
}

export interface ReleaseTrack {
  id: number;
  disc: number;
  position: number;
  title: string;
  duration: number | null;
  hasPreview: boolean;
  likesCount: number;
  priceCents: number | null;
  releaseOnly: boolean;
  owned: boolean;
  artists: ReleaseTrackArtist[];
}

export interface ReleaseTrackArtist {
  id: number;
  name: string;
  slug: string;
}

export interface ReleaseArtist {
  id: number;
  name: string;
  slug: string;
}

export interface ReleaseDetail extends ReleaseSummary {
  description: string | null;
  city: string | null;
  isrc?: string | null;
  tracks: ReleaseTrack[];
  completePrice: number | null;
  ownedTrackIds: number[];
  ownedTrackTotal: number;
}

export function mapReleaseSummary(response: ReleaseSummaryResponse): ReleaseSummary {
  const lanes = parseLanes(response.lanes);
  const price = response.price_cents ?? response.pwyw_min_cents ?? null;
  const completePrice =
    Number(response.owned_release ?? 0) === 1
      ? 0
      : typeof response.complete_price_cents === 'number'
        ? response.complete_price_cents
        : price;
  const publishAtDate = response.publish_at ? new Date(response.publish_at) : null;
  const now = new Date();
  const isNew =
    publishAtDate !== null &&
    !Number.isNaN(publishAtDate.getTime()) &&
    publishAtDate <= now &&
    differenceInDays(now, publishAtDate) <= NEW_RELEASE_WINDOW_DAYS;
  const limitedRemaining =
    response.limited_total !== null
      ? Math.max(response.limited_total - response.limited_sold, 0)
      : null;
  const isLimited = response.limited_total !== null;
  const releaseTypeLabel = formatReleaseType(response.release_type);

  const releaseArtists = (response.artists ?? []).map((artist) => ({
    id: artist.id,
    name: artist.name,
    slug: artist.slug,
  }));
  const artistLabel =
    releaseArtists.length > 0
      ? releaseArtists.map((artist) => artist.name).join(', ')
      : response.artist_name;

  return {
    id: response.id,
    slug: response.slug,
    title: response.title,
    artistName: artistLabel,
    artistSlug: response.artist_slug,
    coverUrl: response.cover_url,
    releaseType: response.release_type,
    releaseTypeLabel,
    status: response.status,
    price,
    completePrice,
    currency: response.currency ?? 'KES',
    tippingEnabled: response.tipping_enabled === 1,
    memberOnly: response.member_only === 1,
    laneLabel: lanes[0] ?? null,
    lanes,
    publishAt: response.publish_at,
    isNew,
    isLimited,
    limitedTotal: response.limited_total,
    limitedSold: response.limited_sold,
    limitedRemaining,
    owned: Number(response.owned_release ?? 0) === 1,
    artists: releaseArtists,
    ownedTrackTotal: response.owned_track_total_cents ?? 0,
  };
}

export function mapReleaseDetail(response: ReleaseDetailResponse): ReleaseDetail {
  const base = mapReleaseSummary(response);
  const tracks = (response.tracks ?? []).map((track) => mapReleaseTrack(track, base.owned));
  const completePrice =
    base.owned
      ? 0
      : typeof response.complete_price_cents === 'number'
        ? response.complete_price_cents
        : base.price;

  return {
    ...base,
    completePrice,
    description: response.description ?? null,
    city: response.city ?? null,
    isrc: (response as Record<string, unknown>).isrc as string | null | undefined,
    tracks,
    ownedTrackIds: (response.owned_track_ids ?? []).map((value) => Number(value)).filter(Boolean),
    ownedTrackTotal: response.owned_track_total_cents ?? 0,
  };
}

export function mapReleaseTrack(track: ReleaseTrackResponse, releaseOwned = false): ReleaseTrack {
  const artistEntries = (track.artists ?? (track as unknown as { track_artists?: TrackArtistResponse[] }).track_artists ?? []);
  const artists = artistEntries.map((artist: TrackArtistResponse): ReleaseTrackArtist => ({
    id: artist.id,
    name: artist.name,
    slug: artist.slug,
  }));
  return {
    id: track.id,
    disc: track.disc_no,
    position: track.track_no,
    title: track.title,
    duration: track.duration_sec,
    hasPreview: track.has_preview === 1,
    likesCount: track.likes_count ?? 0,
    priceCents: track.price_cents ?? null,
    releaseOnly: (track.release_only ?? 0) === 1,
    owned: releaseOwned || Number(track.owned ?? 0) === 1,
    artists,
  };
}

export function formatTrackArtists(track: ReleaseTrack, fallback?: string): string {
  if (track.artists.length > 0) {
    return track.artists.map((artist) => artist.name).join(', ');
  }
  return fallback ?? '';
}

export function getReleaseDisplayPrice(release: Pick<ReleaseSummary, 'completePrice' | 'price'>) {
  return release.completePrice ?? release.price ?? null;
}

export function parseLanes(lanes: string | null): string[] {
  if (!lanes) return [];
  try {
    const parsed = JSON.parse(lanes);
    if (Array.isArray(parsed)) {
      return parsed.map((lane) => String(lane)).filter(Boolean);
    }
  } catch (error) {
    console.warn('Failed to parse lanes JSON', error);
  }
  return [];
}

function differenceInDays(a: Date, b: Date) {
  const diff = a.getTime() - b.getTime();
  return diff / (1000 * 60 * 60 * 24);
}

function formatReleaseType(type: string): string {
  switch (type) {
    case 'single':
      return 'Single';
    case 'ep':
      return 'EP';
    case 'album':
      return 'Album';
    case 'beat':
      return 'Beat';
    case 'bundle':
      return 'Bundle';
    default:
      return type;
  }
}

export function formatPriceLabel(
  price: number | null,
  currency: string
): string {
  if (price === null) {
    return 'Free';
  }
  const formatted = new Intl.NumberFormat('en-KE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);

  return `${currency} ${formatted}`;
}
